import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Rocket } from "lucide-react";
import { api } from "../api";
import { useT } from "../i18n";

// Wiederverwendbarer "Fleet: An/Aus"-Switch (Migration 0072): steuert die
// Loop-Flotte remote per PATCH /projects/{id} {fleet_enabled}. Der Watchdog
// liest fleet_enabled je Projekt. Zwei Nutzungsmodi:
//
//  1. Controlled (GoalsPanel im Kanban): Parent haelt den Wert schon aus
//     GET /projects/{id}/goals -> `enabled` + `onToggle` reichen, KEIN
//     eigener Fetch (vermeidet Doppel-Request).
//  2. Self-contained (Projekt-Header, tab-unabhaengig sichtbar auch bei
//     Projekten OHNE Tasks): nur `projectId` -> laedt den Initialwert selbst
//     aus GET /projects/{id}/goals (bestehende ProjectGoalsOut.fleet_enabled,
//     kein neuer Endpoint) und macht das optimistische PATCH + Rollback selbst.
//
// Verhalten identisch in beiden Modi: role=switch, optimistisches PATCH mit
// stillem Rollback bei Fehler (kein Browser-Dialog), gruener Punkt + Badge
// "aktiv", ruhige Atlas-Aesthetik.

interface ControlledProps {
  /** Aktueller Zustand — vom Parent kontrolliert. */
  enabled: boolean;
  /** Wird bei Klick mit dem neuen Zielzustand aufgerufen. Der Parent uebernimmt
   *  optimistisches Update + PATCH + Rollback. */
  onToggle: (next: boolean) => void;
  /** true, solange ein Save laeuft — deaktiviert den Switch. */
  saving?: boolean;
  projectId?: never;
  variant?: "panel" | "inline";
}

interface SelfProps {
  /** Self-contained-Modus: laedt + persistiert den Zustand selbst. */
  projectId: string;
  enabled?: never;
  onToggle?: never;
  saving?: never;
  variant?: "panel" | "inline";
}

type FleetToggleProps = ControlledProps | SelfProps;

export default function FleetToggle(props: FleetToggleProps) {
  const t = useT();
  const variant = props.variant ?? "panel";
  const controlled = props.projectId === undefined;

  // Self-Modus-State (im controlled Modus ungenutzt).
  const [selfEnabled, setSelfEnabled] = useState<boolean | null>(null);
  const [selfSaving, setSelfSaving] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (controlled || !props.projectId) return;
    let live = true;
    api
      .get<{ fleet_enabled: boolean }>(`/projects/${props.projectId}/goals`)
      .then((g) => {
        if (live) setSelfEnabled(!!g.fleet_enabled);
      })
      .catch(() => {
        /* still: ohne Wert rendern wir nichts */
      });
    return () => {
      live = false;
    };
  }, [controlled, props.projectId]);

  // Im Self-Modus noch nicht geladen -> nichts rendern (kein Flackern eines
  // Default-Zustands, der falsch sein koennte).
  const enabled = controlled ? props.enabled : selfEnabled;
  if (!controlled && enabled === null) return null;

  const saving = controlled ? !!props.saving : selfSaving;

  const handleToggle = async () => {
    const current = controlled ? props.enabled : selfEnabled;
    const next = !current;
    if (controlled) {
      props.onToggle(next);
      return;
    }
    // Self-Modus: optimistisches Update + PATCH + stiller Rollback.
    if (selfSaving) return;
    setSelfSaving(true);
    setSelfEnabled(next);
    try {
      await api.patch(`/projects/${props.projectId}`, { fleet_enabled: next });
    } catch {
      if (alive.current) setSelfEnabled(!next);
    } finally {
      if (alive.current) setSelfSaving(false);
    }
  };

  return (
    <div
      className={clsx(
        "flex items-center gap-2",
        variant === "panel"
          ? "justify-between pb-2 border-b border-cream-200/70 dark:border-ink-600/70"
          : "shrink-0",
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <Rocket
          className={clsx(
            "w-3.5 h-3.5 shrink-0",
            enabled ? "text-emerald-500" : "text-ink-50/40",
          )}
        />
        <span className="text-[11px] uppercase tracking-wider text-ink-50/50">
          {t.shared.flotte.titel}
        </span>
        {enabled && (
          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            {t.shared.flotte.aktiv}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={handleToggle}
        disabled={saving}
        role="switch"
        aria-checked={!!enabled}
        aria-label={t.shared.flotte.schalterAria}
        title={enabled ? t.shared.flotte.titelAn : t.shared.flotte.titelAus}
        className={clsx(
          "relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors disabled:opacity-50",
          enabled ? "bg-emerald-500" : "bg-cream-300 dark:bg-ink-600",
        )}
      >
        <span
          className={clsx(
            "inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform",
            enabled ? "translate-x-3.5" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}
